import { useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { Dropdown, Flex, Spin, Tooltip } from "antd"
import { CalendarOutlined, MoreOutlined, UserOutlined, UpOutlined, DownOutlined } from "@ant-design/icons"
import { DSIcon } from "ui/Icon/DSIcon"
import IconWithBg from "ui/IconWithBg"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import { notifyWaiver } from "store/digital-waiver"
import { toaster } from "components/common/Toast"
import { CHECK_IN_STATUS_LABEL } from "../../../CheckIn/checkInUtils"
import BookingTag from "../../../BookingTag"
import WaiverStatusTag from "../WaiverStatusTag/WaiverStatusTag"
import AnswerAccordion from "../AnswerAccordion/AnswerAccordion"
import styles from "../../index.module.css"
import { spacings } from "themes/variables"

const ExperienceCard = ({
  experience = {},
  waivers = [],
  waiversLoading = false,
  answers = [],
  answersLoading = false,
  bookingUuid,
  onRefresh,
  checkInState = "pending",
  checkedInCount = 0,
  totalGuests = 0
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm"].includes(breakpoint)
  const [isExpanded, setIsExpanded] = useState(true)
  const signedCount = waivers.filter((waiver) => waiver.status === "Signed").length
  const totalWaivers = waivers.length

  const toggleExpanded = () => setIsExpanded((prev) => !prev)

  // Guest count + check-in status + docs badge — shared by both header layouts.
  const statsRow = (
    <Flex align="center" gap={8} className={styles.expStats}>
      <Tooltip title={CHECK_IN_STATUS_LABEL[checkInState]}>
        {/* Read-only status on the detail page — check-in is done from the listings */}
        <span className={styles.checkInStatus}>
          <DSIcon name="checkIn" variant={checkInState === "pending" ? "pending" : "active"} width={52} height={25} />
          {checkInState === "partial" && <span className={styles.checkInCount}>{checkedInCount}/{totalGuests}</span>}
        </span>
      </Tooltip>
      <Flex align="center" gap={4}>
        <UserOutlined className={styles.metaIcon} />
        <span className={styles.metaText}>{t("booking.reservation_detail.adults_count", { count: experience.adults })}</span>
      </Flex>
      <BookingTag color={totalWaivers > 0 && signedCount === totalWaivers ? "success" : "error"}>
        <DSIcon name="file" width={14} height={14} fill="currentColor" />
        {signedCount}/{totalWaivers}
      </BookingTag>
    </Flex>
  )

  const handleWaiverAction = (key, waiver) => {
    if (key === "download") {
      if (waiver.documentUrl) window.open(waiver.documentUrl, "_blank", "noopener,noreferrer")
      return
    }
    if (key === "resend" && bookingUuid) {
      dispatch(notifyWaiver(bookingUuid, { notify_by: "email", email: waiver.guestEmail })).then((res) => {
        toaster.success({ title: res?.message || "Waiver request sent" })
        onRefresh?.()
      })
    }
  }

  // Row/card actions menu (download signed waivers, resend unsigned) — shared by table + mobile cards.
  const renderWaiverActions = (waiver) => (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      menu={{
        onClick: ({ key }) => handleWaiverAction(key, waiver),
        items:
          waiver.status === "Signed"
            ? [{ key: "download", label: t("booking.reservation_detail.download") }]
            : [{ key: "resend", label: t("booking.reservation_detail.resend") }]
      }}
    >
      <button type="button" className={styles.iconBtn}>
        <MoreOutlined />
      </button>
    </Dropdown>
  )

  return (
    <div className={styles.card}>
      {/* Experience section — mobile stacks the details under the code/tag row with the total inline */}
      {isMobile ? (
        <Flex align="flex-start" gap={12}>
          <img src={experience.imageUrl} alt={experience.title} className={styles.expImage} />
          <Flex vertical gap={4} className={styles.expBody}>
            <Flex align="center" justify="space-between" gap={8}>
              <Flex align="center" gap={8}>
                <span className={styles.expCode}>{experience.id}</span>
                <BookingTag color="blue">{experience.status}</BookingTag>
              </Flex>
              <IconWithBg icon={isExpanded ? <UpOutlined /> : <DownOutlined />} onClick={toggleExpanded} />
            </Flex>
            <span className={styles.expTitle}>{experience.title}</span>
            <Flex align="center" gap={6} className={styles.expMeta}>
              <CalendarOutlined className={styles.metaIcon} />
              <span className={styles.metaText}>
                {experience.date} · {experience.timeStart} - {experience.timeEnd}
              </span>
            </Flex>
            <Flex align="center" gap={4}>
              <UserOutlined className={styles.metaIcon} />
              <span className={styles.metaText}>{t("booking.reservation_detail.adults_count", { count: experience.adults })}</span>
            </Flex>
            <Flex align="center" gap={6}>
              <span className={styles.expTotalLabel}>{t("booking.reservation_detail.total")}</span>
              <span className={styles.expTotalInline}>${(experience.total ?? 0).toFixed(2)}</span>
            </Flex>
            {statsRow}
          </Flex>
        </Flex>
      ) : (
        <Flex align="flex-start" justify="space-between" gap={12}>
          <Flex align="flex-start" gap={12}>
            <img src={experience.imageUrl} alt={experience.title} className={styles.expImage} />
            <Flex vertical gap={4}>
              <Flex align="center" gap={8}>
                <span className={styles.expTitle}>{experience.title}</span>
                <BookingTag color="blue">{experience.status}</BookingTag>
              </Flex>
              <Flex align="center" gap={6} className={styles.expMeta}>
                <span className={styles.expCode}>{experience.id}</span>
                <span className={styles.expDot}>·</span>
                <CalendarOutlined className={styles.metaIcon} />
                <span className={styles.metaText}>
                  {experience.date} · {experience.timeStart} - {experience.timeEnd}
                </span>
              </Flex>
              {statsRow}
            </Flex>
          </Flex>
          <Flex align="flex-end" gap={8} className={styles.expTotalCol}>
            <Flex vertical gap={6}>
              <span className={styles.expTotalLabel}>{t("booking.reservation_detail.total")}</span>
              <span className={styles.expTotal}>${(experience.total ?? 0).toFixed(2)}</span>
            </Flex>
            <IconWithBg icon={isExpanded ? <UpOutlined /> : <DownOutlined />} onClick={toggleExpanded} />
          </Flex>
        </Flex>
      )}

      {isExpanded && (
        <>
          {/* Waivers & Documents section */}
          <div className={styles.sectionDivider} />
          <Flex align="center" justify="space-between" className={styles.cardHeader}>
            <span className={styles.cardTitle}>
              {t("booking.reservation_detail.waivers_documents", { signed: signedCount, total: waivers.length })}
            </span>
          </Flex>
          {isMobile ? (
            <Flex vertical gap={12} className={styles.waiverCardList}>
              {waiversLoading && waivers.length === 0 && (
                <Flex justify="center" className="p-m"><Spin size="small" /></Flex>
              )}
              {!waiversLoading && waivers.length === 0 && (
                <span className={styles.emailCell}>{t("booking.reservation_detail.no_waivers", "No waivers added.")}</span>
              )}
              {waivers.map((waiver) => (
                <div key={waiver.id} className={styles.waiverCard}>
                  <Flex align="center" justify="space-between" gap={8}>
                    <Flex align="center" gap={8} className={styles.waiverCardHead}>
                      <DSIcon name="file" width={16} height={16} />
                      <span className={styles.waiverCardDoc}>{waiver.document}</span>
                      <WaiverStatusTag status={waiver.status} />
                    </Flex>
                    {renderWaiverActions(waiver)}
                  </Flex>
                  <span className={styles.waiverCardName}>
                    {waiver.fullName}
                    {waiver.age != null && waiver.age !== "" && `, ${t("booking.reservation_detail.waiver_age", { age: waiver.age })}`}
                  </span>
                  <span className={styles.waiverCardContact}>
                    {[waiver.guestEmail, waiver.phone && waiver.phone !== "-" ? waiver.phone : null].filter(Boolean).join(" · ")}
                  </span>
                </div>
              ))}
            </Flex>
          ) : (
            <div className={styles.waiverTableWrap}>
            <table className={styles.waiverTable}>
              <thead>
                <tr>
                  <th>{t("booking.reservation_detail.col_full_name")}</th>
                  <th>{t("booking.reservation_detail.col_guest_email")}</th>
                  <th>{t("booking.reservation_detail.col_phone")}</th>
                  <th>{t("booking.reservation_detail.col_document")}</th>
                  <th>
                    {t("booking.reservation_detail.col_age")} <span className={styles.sortIcon}>↕</span>
                  </th>
                  <th>
                    {t("booking.reservation_detail.col_status")} <span className={styles.sortIcon}>↕</span>
                  </th>
                  <th>{t("booking.reservation_detail.col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {waiversLoading && waivers.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <Flex justify="center" className="p-m"><Spin size="small" /></Flex>
                    </td>
                  </tr>
                )}
                {!waiversLoading && waivers.length === 0 && (
                  <tr>
                    <td colSpan={7} className={styles.emailCell}>{t("booking.reservation_detail.no_waivers", "No waivers added.")}</td>
                  </tr>
                )}
                {waivers.map((waiver) => (
                  <tr key={waiver.id}>
                    <td>{waiver.fullName}</td>
                    <td className={styles.emailCell}>{waiver.guestEmail}</td>
                    <td>{waiver.phone ?? "-"}</td>
                    <td>
                      <Flex align="center" gap={4}>
                        <DSIcon name="file" width={14} height={14} />
                        <span>{waiver.document}</span>
                      </Flex>
                    </td>
                    <td>{waiver.age}</td>
                    <td>
                      <WaiverStatusTag status={waiver.status} />
                    </td>
                    <td>{renderWaiverActions(waiver)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
          {!isMobile && (
          <Flex align="center" justify="flex-end" gap={spacings.L} className={styles.waiverPagination}>
            <Flex align="center" gap={8}>
              <span className={styles.paginationLabel}>{t("booking.reservation_detail.rows_per_page", { count: 10 })}</span>
              <span className={styles.paginationLabel}>▾</span>
            </Flex>
            <Flex align="center" gap={8}>
              <span className={styles.paginationLabel}>1-2 of 2</span>
              <button type="button" className={styles.pageBtn} disabled>
                ‹
              </button>
              <button type="button" className={styles.pageBtn} disabled>
                ›
              </button>
            </Flex>
          </Flex>
          )}

          {/* Answers & Preferences section */}
          <div className={styles.sectionDivider} />
          <span className={styles.cardTitle}>{t("booking.reservation_detail.answers_preferences")}</span>
          <Flex vertical gap={1} className={styles.accordionList}>
            {answersLoading && answers.length === 0 && (
              <Flex justify="center" className="p-m"><Spin size="small" /></Flex>
            )}
            {!answersLoading && answers.length === 0 && (
              <span className={styles.emailCell}>{t("booking.reservation_detail.no_answers", "No answers submitted.")}</span>
            )}
            {answers.map((item) => (
              <AnswerAccordion key={item.id} item={item} />
            ))}
          </Flex>
        </>
      )}
    </div>
  )
}

ExperienceCard.propTypes = {
  experience: PropTypes.object,
  waivers: PropTypes.array,
  waiversLoading: PropTypes.bool,
  answers: PropTypes.array,
  answersLoading: PropTypes.bool,
  bookingUuid: PropTypes.string,
  onRefresh: PropTypes.func,
  checkInState: PropTypes.oneOf(["pending", "partial", "in", "out"]),
  checkedInCount: PropTypes.number,
  totalGuests: PropTypes.number
}

export default ExperienceCard
